import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoJRS from '../assets/logo_jrs.png';
import api, {
  getRegistros, getFuncionariosResumo, getDepartamentos,
  getSaldoHora, getSaldosHora, saveSaldoHora, updateSaldoHora, deleteSaldoHora,
  getJornadas, updateJornada, getFeriados, createAfastamento,
} from '../services/api';

const decToHHMM = v => {
  if (v == null) return '—';
  const neg = v < 0;
  const abs = Math.abs(v);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${neg ? '-' : ''}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const isoToBR = iso => { if (!iso || !iso.includes('-')) return iso; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
const brToISO = br => { if (!br || !br.includes('/')) return br; const [d, m, y] = br.split('/'); return `${y}-${m}-${d}`; };
const hoje = () => new Date().toISOString().slice(0, 10);
const primeiroDiaMes = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

const SIGLA_DIA = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
const getSigla = dataBR => {
  if (!dataBR) return null;
  const [d, m, y] = dataBR.split('/');
  return SIGLA_DIA[new Date(`${y}-${m}-${d}T00:00:00`).getDay()];
};

const TIPO_LABELS = {
  ABONO: 'Abono', AJUSTE_JORNADA: 'Ajuste de Jornada', COMPENSAR_DEPOIS: 'Compensar depois',
};

export default function SaldoNegativo() {
  const [lista, setLista] = useState([]);
  const [funcs, setFuncs] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [filtros, setFiltros] = useState({ funcionario: '', data_inicio: primeiroDiaMes(), data_fim: hoje(), classificado: 'nao', departamento: '' });
  const [agruparPor, setAgruparPor] = useState('');
  const [loading, setLoading] = useState(false);

  // modal classificação (gestor)
  const [saldoModal, setSaldoModal] = useState(false);
  const [saldoReg, setSaldoReg] = useState(null);
  const [saldoForm, setSaldoForm] = useState(null);
  const [saldoId, setSaldoId] = useState(null);
  const [saldoRecord, setSaldoRecord] = useState(null);
  const [jornadaAjuste, setJornadaAjuste] = useState(null);
  const [ajusteHoras, setAjusteHoras] = useState('');
  const [ajusteHistorico, setAjusteHistorico] = useState(null);

  // modal proposta (funcionário)
  const [propostaModal, setPropostaModal] = useState(false);
  const [propostaReg, setPropostaReg] = useState(null);
  const [propostaComps, setPropostaComps] = useState([]);
  const [propostaSaving, setPropostaSaving] = useState(false);
  const [propostaSaldoId, setPropostaSaldoId] = useState(null);
  const [propostaNumero, setPropostaNumero] = useState('');
  const [propostaMotivoAusencia, setPropostaMotivoAusencia] = useState('');
  const [propostaDocumento, setPropostaDocumento] = useState(null);
  const [propostaDocumentoExistente, setPropostaDocumentoExistente] = useState('');
  const [propostaDocPeriodo, setPropostaDocPeriodo] = useState({ data_inicio: '', hora_inicio: '', data_fim: '', hora_fim: '' });

  const [feriadosSet, setFeriadosSet] = useState(new Set());
  const [propostasPorRegistro, setPropostasPorRegistro] = useState({}); // { registroId: saldoRecord }

  useEffect(() => { getFuncionariosResumo().then(r => setFuncs(r.data)); }, []);
  useEffect(() => { getDepartamentos({ ativo: true }).then(r => setDepartamentos(r.data.results || r.data)).catch(() => {}); }, []);
  useEffect(() => { getFeriados({ page_size: 500 }).then(r => { const list = r.data.results || r.data; setFeriadosSet(new Set(list.map(f => f.data))); }).catch(() => {}); }, []);

  const carregarPropostas = useCallback(() => {
    getSaldosHora({ tipo: 'COMPENSAR_DEPOIS', page_size: 500 }).then(r => {
      const lista = r.data.results || r.data;
      const mapa = {};
      lista.forEach(s => { if (s.registro) mapa[s.registro] = s; });
      setPropostasPorRegistro(mapa);
    }).catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        data_inicio: isoToBR(filtros.data_inicio),
        data_fim: isoToBR(filtros.data_fim),
      };
      if (filtros.funcionario) params.funcionario = filtros.funcionario;
      // busca todas as páginas
      const primeira = await getRegistros({ ...params, page_size: 200 });
      const data0 = primeira.data;
      let todos = data0.results || data0;
      let next = data0.next || null;
      while (next) {
        // extrai path+query do next (ex: http://localhost:8000/api/registros/?page=2&...)
        const nextPath = next.replace(/^https?:\/\/[^/]+/, '').replace('/api', '');
        const resp = await api.get(nextPath);
        todos = todos.concat(resp.data.results || resp.data);
        next = resp.data.next || null;
      }
      const negativos = todos.filter(reg =>
        reg.saldo_horas != null &&
        reg.saldo_horas < 0 &&
        Math.round(Math.abs(reg.saldo_horas) * 60) > 10
      );
      setLista(negativos);
    } finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarPropostas(); }, [carregarPropostas]);

  // filtro classificação + departamento (client-side)
  const listaFiltrada = lista.filter(r => {
    if (filtros.classificado === 'sim' && !r.saldo_tipo) return false;
    if (filtros.classificado === 'nao' && r.saldo_tipo) return false;
    if (filtros.departamento) {
      const func = funcs.find(f => String(f.id) === String(r.funcionario));
      if (!func || String(func.departamento) !== String(filtros.departamento)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (agruparPor === 'funcionario') {
      const nCmp = (a.funcionario_nome || '').localeCompare(b.funcionario_nome || '', 'pt-BR');
      return nCmp !== 0 ? nCmp : (b.data || '').localeCompare(a.data || '');
    }
    return (b.data || '').localeCompare(a.data || '');
  });

  // ── modal ──
  const carregarJornada = async reg => {
    setJornadaAjuste(null); setAjusteHoras(''); setAjusteHistorico(null);
    try {
      const r = await getJornadas({ funcionario: reg.funcionario, ativo: true });
      const jornada = (r.data.results || r.data)[0];
      if (!jornada) return;
      const sigla = getSigla(reg.data);
      const horario = (jornada.horarios_dias || {})[sigla] || {};
      setJornadaAjuste({ id: jornada.id, sigla });
      let horasStr = '';
      if (horario.carga_diaria != null) {
        const h = Math.floor(horario.carga_diaria);
        const mx = Math.round((horario.carga_diaria - h) * 60);
        horasStr = `${String(h).padStart(2, '0')}:${String(mx).padStart(2, '0')}`;
      } else if (horario.entrada && horario.saida) {
        const [eh, em] = horario.entrada.split(':').map(Number);
        const [sh, sm] = horario.saida.split(':').map(Number);
        let total = (sh * 60 + sm) - (eh * 60 + em);
        if (horario.inicio_almoco && horario.fim_almoco) {
          const [iah, iam] = horario.inicio_almoco.split(':').map(Number);
          const [fah, fam] = horario.fim_almoco.split(':').map(Number);
          total -= (fah * 60 + fam) - (iah * 60 + iam);
        }
        if (total > 0) { const h = Math.floor(total / 60), mx = total % 60; horasStr = `${String(h).padStart(2, '0')}:${String(mx).padStart(2, '0')}`; }
      }
      setAjusteHoras(horasStr);
      if (horasStr) setAjusteHistorico({ horas: horasStr });
    } catch (_) {}
  };

  const abrirSaldo = async reg => {
    setSaldoReg(reg); setSaldoId(null); setSaldoRecord(null);
    setSaldoForm({ tipo: 'ABONO', autorizado: false, data_referencia: '', compensacoes: [], observacao: '', abonado_por: '' });
    try {
      const r = await getSaldoHora(reg.id);
      const existing = (r.data.results || r.data)[0];
      if (existing) {
        setSaldoId(existing.id); setSaldoRecord(existing);
        const obs = existing.observacao || '';
        const cargaMatch = obs.match(/^\[CARGA:(\d{2}:\d{2})\]\s*/);
        const cargaHoras = cargaMatch ? cargaMatch[1] : null;
        let obsRest = cargaMatch ? obs.replace(cargaMatch[0], '') : obs;
        const abonoMatch = obsRest.match(/^\[ABONADO_POR:([^\]]+)\]\s*/);
        const abonadoPor = abonoMatch ? abonoMatch[1] : '';
        const obsLimpa = abonoMatch ? obsRest.replace(abonoMatch[0], '') : obsRest;
        const saldoMin = Math.abs(Math.round(reg.saldo_horas * 60));
        setSaldoForm({
          tipo: existing.tipo,
          autorizado: existing.autorizado || false,
          data_referencia: existing.data_referencia ? brToISO(existing.data_referencia) : '',
          compensacoes: (existing.compensacoes && existing.compensacoes.length)
            ? existing.compensacoes
            : (existing.tipo === 'COMPENSAR_DEPOIS' ? [{ data: '', minutos: Math.min(saldoMin, 60) }] : []),
          observacao: obsLimpa,
          abonado_por: abonadoPor,
        });
        if (existing.tipo === 'AJUSTE_JORNADA') {
          if (cargaHoras) { setAjusteHoras(cargaHoras); setAjusteHistorico({ horas: cargaHoras }); }
          carregarJornada(reg);
        }
      }
    } catch (_) {}
    setSaldoModal(true);
  };

  const mudarTipo = tipo => {
    const saldoMin = saldoReg ? Math.abs(Math.round(saldoReg.saldo_horas * 60)) : 60;
    setSaldoForm(f => ({
      ...f, tipo,
      compensacoes: tipo === 'COMPENSAR_DEPOIS' && !f.compensacoes.length
        ? [{ data: '', minutos: Math.min(saldoMin, 60) }]
        : f.compensacoes,
    }));
    if (tipo === 'AJUSTE_JORNADA' && saldoReg) carregarJornada(saldoReg);
  };

  const addComp = () => setSaldoForm(f => {
    const saldoMin = saldoReg ? Math.abs(Math.round(saldoReg.saldo_horas * 60)) : 0;
    const usado = f.compensacoes.reduce((s, c) => s + (parseInt(c.minutos) || 0), 0);
    const restante = Math.max(0, saldoMin - usado);
    if (restante <= 0) return f;
    return { ...f, compensacoes: [...f.compensacoes, { data: '', minutos: Math.min(restante, 60) }] };
  });
  const removeComp = i => setSaldoForm(f => {
    const next = f.compensacoes.filter((_, idx) => idx !== i);
    return { ...f, compensacoes: next.length ? next : [{ data: '', minutos: 60 }] };
  });
  const updateComp = (i, field, value) => setSaldoForm(f => {
    const list = [...f.compensacoes]; list[i] = { ...list[i], [field]: value }; return { ...f, compensacoes: list };
  });
  const isSexta = iso => { if (!iso) return true; return new Date(iso + 'T00:00:00').getDay() === 5; };

  // ── Proposta (funcionário) ──
  const gerarNumeroProposta = async () => {
    const d = new Date();
    const anoMes = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
    try {
      const r = await getSaldosHora({ tipo: 'COMPENSAR_DEPOIS', page_size: 500 });
      const todos = r.data.results || r.data;
      const regex = new RegExp(`\\[PROPOSTA:${anoMes}-(\\d{4})\\]`);
      let maxSeq = 0;
      todos.forEach(s => { const m = (s.observacao || '').match(regex); if (m) maxSeq = Math.max(maxSeq, parseInt(m[1])); });
      return `${anoMes}-${String(maxSeq + 1).padStart(4, '0')}`;
    } catch (_) { return `${anoMes}-0001`; }
  };

  const abrirProposta = async (reg) => {
    setPropostaReg(reg);
    setPropostaSaldoId(null);
    setPropostaNumero('');
    const saldoMin = Math.abs(Math.round(reg.saldo_horas * 60));
    setPropostaComps([{ data: '', minutos: Math.min(saldoMin, 60) }]);
    setPropostaMotivoAusencia('');
    setPropostaDocumento(null);
    setPropostaDocumentoExistente('');
    setPropostaDocPeriodo({ data_inicio: brToISO(reg.data) || '', hora_inicio: '', data_fim: brToISO(reg.data) || '', hora_fim: '' });
    try {
      const r = await getSaldoHora(reg.id);
      const existing = (r.data.results || r.data)[0];
      if (existing && existing.tipo === 'COMPENSAR_DEPOIS' && existing.autorizado === false) {
        setPropostaSaldoId(existing.id);
        setPropostaComps(existing.compensacoes?.length ? existing.compensacoes : [{ data: '', minutos: Math.min(saldoMin, 60) }]);
        setPropostaMotivoAusencia(existing.motivo_ausencia || '');
        setPropostaDocumentoExistente(existing.documento || '');
        const mNum = (existing.observacao || '').match(/\[PROPOSTA:(\d{6}-\d{4})\]/);
        setPropostaNumero(mNum ? mNum[1] : '');
      }
    } catch (_) {}
    setPropostaModal(true);
  };

  const salvarProposta = async () => {
    const ehJustificado = propostaMotivoAusencia === 'JUSTIFICADO';
    if (!ehJustificado && propostaComps.some(c => !c.data)) {
      alert('Informe todas as datas de compensação.'); return;
    }
    setPropostaSaving(true);
    try {
      const numero = (propostaSaldoId && propostaNumero) ? propostaNumero : await gerarNumeroProposta();
      const fd = new FormData();
      fd.append('registro', propostaReg.id);
      fd.append('tipo', 'COMPENSAR_DEPOIS');
      fd.append('autorizado', false);
      fd.append('motivo_ausencia', propostaMotivoAusencia);
      const docPeriodoTag = ehJustificado && propostaDocPeriodo.data_inicio
        ? ` [DOC:${propostaDocPeriodo.data_inicio}T${propostaDocPeriodo.hora_inicio || '00:00'}|${propostaDocPeriodo.data_fim || propostaDocPeriodo.data_inicio}T${propostaDocPeriodo.hora_fim || '00:00'}]`
        : '';
      fd.append('observacao', `[PROPOSTA:${numero}]${docPeriodoTag}`);
      // Apenas INJUSTIFICADO envia datas de compensação
      if (!ehJustificado) {
        fd.append('compensacoes', JSON.stringify(propostaComps));
      } else {
        fd.append('compensacoes', JSON.stringify([]));
      }
      // Documento (apenas JUSTIFICADO)
      if (propostaDocumento) fd.append('documento', propostaDocumento);
      if (propostaSaldoId) await updateSaldoHora(propostaSaldoId, fd);
      else await saveSaldoHora(fd);

      // JUSTIFICADO: cria registro de Afastamento automaticamente
      if (ehJustificado) {
        const afFD = new FormData();
        afFD.append('funcionario', propostaReg.funcionario);
        afFD.append('tipo', 'JUSTIFICADA');
        afFD.append('unidade', propostaDocPeriodo.data_fim && propostaDocPeriodo.data_fim !== propostaDocPeriodo.data_inicio ? 'DIAS' : 'HORAS');
        afFD.append('data_inicio', isoToBR(propostaDocPeriodo.data_inicio || brToISO(propostaReg.data)));
        if (propostaDocPeriodo.data_fim) afFD.append('data_fim', isoToBR(propostaDocPeriodo.data_fim));
        if (propostaDocPeriodo.hora_inicio) afFD.append('hora_inicio', propostaDocPeriodo.hora_inicio);
        if (propostaDocPeriodo.hora_fim) afFD.append('hora_fim', propostaDocPeriodo.hora_fim);
        if (propostaDocumento) afFD.append('documento', propostaDocumento);
        await createAfastamento(afFD);
      }

      setPropostaModal(false);
      carregar(); carregarPropostas();
    } catch (e) {
      alert(JSON.stringify(e.response?.data || 'Erro ao salvar'));
    } finally { setPropostaSaving(false); }
  };

  const cancelarProposta = async (reg) => {
    const saldo = propostasPorRegistro[reg.id];
    if (!saldo) return;
    if (!window.confirm('Cancelar esta proposta de compensação?')) return;
    try {
      await deleteSaldoHora(saldo.id);
      carregarPropostas();
    } catch (e) {
      alert(JSON.stringify(e.response?.data || 'Erro ao cancelar proposta'));
    }
  };

  const addPropostaComp = () => {
    const saldoMin = propostaReg ? Math.abs(Math.round(propostaReg.saldo_horas * 60)) : 0;
    const usado = propostaComps.reduce((s, c) => s + (parseInt(c.minutos) || 0), 0);
    const restante = Math.max(0, saldoMin - usado);
    if (restante <= 0) return;
    setPropostaComps(cs => [...cs, { data: '', minutos: Math.min(restante, 60) }]);
  };
  const removePropostaComp = i => setPropostaComps(cs => { const next = cs.filter((_, idx) => idx !== i); return next.length ? next : [{ data: '', minutos: 60 }]; });
  const updatePropostaComp = (i, field, value) => setPropostaComps(cs => { const list = [...cs]; list[i] = { ...list[i], [field]: value }; return list; });

  const salvarSaldo = async () => {
    let obsBase = saldoForm.observacao;
    if (saldoForm.tipo === 'AJUSTE_JORNADA' && ajusteHoras)
      obsBase = `[CARGA:${ajusteHoras}] ${obsBase}`.trim();
    if (saldoForm.tipo === 'ABONO' && saldoForm.abonado_por)
      obsBase = `[ABONADO_POR:${saldoForm.abonado_por.trim()}] ${obsBase}`.trim();
    const payload = {
      registro: saldoReg.id, tipo: saldoForm.tipo,
      autorizado: saldoForm.tipo === 'HORA_EXTRA' ? saldoForm.autorizado : null,
      data_referencia: saldoForm.tipo === 'COMPENSACAO' && saldoForm.data_referencia ? isoToBR(saldoForm.data_referencia) : null,
      compensacoes: saldoForm.tipo === 'COMPENSAR_DEPOIS' ? saldoForm.compensacoes : [],
      observacao: obsBase,
    };
    try {
      if (saldoForm.tipo === 'AJUSTE_JORNADA') {
        const jResp = await getJornadas({ funcionario: saldoReg.funcionario, ativo: 'true' });
        const jornada = (jResp.data.results || jResp.data)[0];
        if (jornada) {
          const jId = jornadaAjuste?.id || jornada.id;
          const sigla = jornadaAjuste?.sigla || getSigla(saldoReg.data);
          const horarios = { ...(jornada.horarios_dias || {}) };
          let carga = 0;
          if (ajusteHoras) { const [h, m] = ajusteHoras.split(':').map(Number); carga = h + m / 60; }
          horarios[sigla] = { ...(horarios[sigla] || {}), carga_diaria: carga };
          await updateJornada(jId, { ...jornada, horarios_dias: horarios });
        }
      }
      if (saldoId) await updateSaldoHora(saldoId, payload);
      else await saveSaldoHora(payload);
      setSaldoModal(false); carregar();
    } catch (e) { alert(JSON.stringify(e.response?.data || 'Erro ao salvar')); }
  };

  const TIPO_NEG = [
    { value: 'ABONO',            label: 'Abono',             icon: 'bi-shield-check' },
    { value: 'AJUSTE_JORNADA',   label: 'Ajuste de Jornada', icon: 'bi-pencil-square' },
    { value: 'COMPENSAR_DEPOIS', label: 'Compensar depois',  icon: 'bi-calendar-plus' },
  ];

  const minSaldo = saldoReg ? Math.abs(Math.round(saldoReg.saldo_horas * 60)) : 0;
  const totalComp = saldoForm?.compensacoes?.reduce((s, c) => s + (!c.nao_realizado ? (parseInt(c.minutos) || 0) : 0), 0) || 0;
  const compOk = totalComp <= minSaldo;

  const gerarPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const ML = 14, MR = 14;

    const depto = filtros.departamento
      ? (departamentos.find(d => String(d.id) === String(filtros.departamento))?.nome || 'Todos')
      : 'Todos';

    const addHeader = () => {
      // Fundo branco
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pw, 31, 'F');

      // Linha azul no topo
      doc.setFillColor(29, 78, 216);
      doc.rect(0, 0, pw, 1.5, 'F');

      // ── Logomarca (esquerda) — 30% menor ──
      const logoW = 31.5;
      const logoH = 12.6;
      const logoX = ML;
      const logoY = 7;
      doc.addImage(logoJRS, 'PNG', logoX, logoY, logoW, logoH);

      // ── Título (à direita da logo) ──
      const titleX = ML + logoW + 7;
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 30, 55);
      doc.text('Relatório de Saldo Negativo', titleX, 11);

      // Linha fina separando título das pills
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.25);
      doc.line(titleX, 14, pw - MR, 14);

      // ── Info em linha: label cinza | valor bold colorido — com separadores ──
      const infoY = 20;
      doc.setFontSize(7);

      const drawInfo = (label, value, x, txR, txG, txB) => {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150, 155, 165);
        doc.text(label, x, infoY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(txR, txG, txB);
        const lw = doc.getTextWidth(label);
        doc.text(value, x + lw + 1.5, infoY);
        return x + lw + 1.5 + doc.getTextWidth(value);
      };

      let ix = titleX;
      ix = drawInfo('Período  ', `${isoToBR(filtros.data_inicio)} – ${isoToBR(filtros.data_fim)}`, ix, 29, 78, 216);
      // separador vertical
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.3);
      doc.line(ix + 4, infoY - 4.5, ix + 4, infoY + 0.5);
      ix += 9;
      ix = drawInfo('Depto.  ', depto, ix, 21, 128, 61);
      doc.line(ix + 4, infoY - 4.5, ix + 4, infoY + 0.5);
      ix += 9;
      drawInfo('Total  ', `${listaFiltrada.length} registro(s)`, ix, 71, 85, 105);

      // Linha inferior do cabeçalho
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(0, 28, pw, 28);
    };
    addHeader();

    // Agrupar e ordenar datas decrescentes
    const grupos = {};
    listaFiltrada.forEach(r => {
      if (!grupos[r.funcionario_nome]) grupos[r.funcionario_nome] = [];
      grupos[r.funcionario_nome].push(r);
    });
    Object.values(grupos).forEach(regs => regs.sort((a, b) => (b.data || '').localeCompare(a.data || '')));
    const gruposOrdenados = Object.entries(grupos).sort(([a], [b]) => a.localeCompare(b, 'pt-BR'));

    let y = 32;

    const colStyles = {
      0: { cellWidth: 26, textColor: [100, 116, 139] },
      1: { cellWidth: 24, halign: 'center', fontStyle: 'bold', textColor: [29, 78, 216] },
      2: { cellWidth: 22, halign: 'center', fontStyle: 'bold', textColor: [153, 27, 27] },
      3: { cellWidth: 22, halign: 'center' },
      4: { cellWidth: 'auto', halign: 'center' },
    };

    gruposOrdenados.forEach(([nome, regs], gi) => {
      const totalMin = regs.reduce((s, r) => s + Math.round(Math.abs(r.saldo_horas) * 60), 0);
      const th = Math.floor(totalMin / 60), tm = totalMin % 60;
      const totalStr = `-${String(th).padStart(2,'0')}:${String(tm).padStart(2,'0')}`;

      // Verificar espaço na página (grupo pode precisar de ao menos 2 linhas)
      const needed = 8 + (regs.length * 6) + 8;
      if (y + needed > ph - 16) {
        doc.addPage();
        addHeader();
        y = 32;
      }

      // ── Cabeçalho do funcionário ──
      const barH = 9;
      const tableW = pw - ML - MR;

      // Fundo do card
      doc.setFillColor(235, 241, 252);
      doc.rect(ML, y, tableW, barH, 'F');

      // Ícone de usuário (cabeça + corpo)
      const iconCX = ML + 3.5;
      const iconCY = y + barH / 2;
      doc.setFillColor(29, 78, 216);
      doc.circle(iconCX, iconCY - 1.5, 0.9, 'F');   // cabeça
      doc.ellipse(iconCX, iconCY + 0.8, 1.3, 0.8, 'F'); // corpo/ombros

      // Nome (maiúsculo)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(15, 30, 55);
      const textY = y + barH / 2 + 1.6;
      doc.text(nome.toUpperCase(), ML + 9, textY);

      // Badge no canto direito
      const badgeLabel = `${regs.length} Registro${regs.length > 1 ? 's' : ''}`;
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      const badgeH = 4.5;
      const badgeW = doc.getTextWidth(badgeLabel) + 5;
      const badgeX = ML + tableW - badgeW - 2;
      const badgeY = y + (barH - badgeH) / 2;
      doc.setFillColor(55, 65, 81);
      doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 1.1, 1.1, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(badgeLabel, badgeX + badgeW / 2, badgeY + badgeH * 0.7, { align: 'center' });

      // Total (relógio + valor) à esquerda do badge
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(185, 28, 28);
      const totalW = doc.getTextWidth(totalStr);
      const totalTextX = badgeX - totalW - 7;
      doc.text(totalStr, totalTextX, textY);

      // Ícone de relógio antes do total
      const clkCX = totalTextX - 4;
      const clkCY = textY - 1.4;
      const clkR = 1.8;
      doc.setDrawColor(185, 28, 28);
      doc.setLineWidth(0.35);
      doc.circle(clkCX, clkCY, clkR, 'S');
      doc.line(clkCX, clkCY, clkCX, clkCY - clkR * 0.7);       // ponteiro hora
      doc.line(clkCX, clkCY, clkCX + clkR * 0.6, clkCY + 0.4); // ponteiro minuto

      y += barH;

      // ── Tabela do funcionário ──
      autoTable(doc, {
        startY: y,
        margin: { left: ML, right: MR },
        tableWidth: pw - ML - MR,
        head: [['Data', 'Realizado', 'Saldo', 'Status', 'Classificação']],
        body: regs.map(r => [
          r.data || '',
          r.horas_trabalhadas != null ? decToHHMM(r.horas_trabalhadas) : '—',
          decToHHMM(r.saldo_horas),
          r.status_display || r.status || '',
          r.saldo_tipo ? (TIPO_LABELS[r.saldo_tipo] || r.saldo_tipo) : 'Pendente',
        ]),
        styles: {
          fontSize: 7,
          cellPadding: { top: 1.5, bottom: 1.5, left: 4, right: 4 },
          lineColor: [226, 232, 240],
          lineWidth: 0.15,
          textColor: [51, 65, 85],
        },
        headStyles: {
          fillColor: [248, 250, 252],
          textColor: [100, 116, 139],
          fontStyle: 'bold',
          fontSize: 6.5,
          lineColor: [209, 213, 219],
          lineWidth: 0.3,
          cellPadding: { top: 1.5, bottom: 1.5, left: 4, right: 4 },
        },
        columnStyles: colStyles,
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });

      y = doc.lastAutoTable.finalY + (gi < gruposOrdenados.length - 1 ? 5 : 0);
    });

    // ── Total geral ──
    y += 3;
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.4);
    doc.line(ML, y, pw - MR, y);
    y += 5;

    const totalGeralMin = listaFiltrada.reduce((s, r) => s + Math.round(Math.abs(r.saldo_horas) * 60), 0);
    const tgh = Math.floor(totalGeralMin / 60), tgm = totalGeralMin % 60;
    const totalGeralStr = `-${String(tgh).padStart(2,'0')}:${String(tgm).padStart(2,'0')}`;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Total geral em débito:', pw - MR, y, { align: 'right' });
    const labelW = doc.getTextWidth('Total geral em débito:');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(153, 27, 27);
    doc.text(totalGeralStr, pw - MR, y + 5, { align: 'right' });

    // ── Rodapé ──
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(203, 213, 225);
    doc.text(
      `Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`,
      ML, ph - 6
    );

    doc.save(`saldo_negativo_${filtros.data_inicio}_${filtros.data_fim}.pdf`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
          <i className="bi bi-graph-down-arrow" />
        </div>
        <div>
          <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Gestão de Pessoal · Controle de Ponto</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Saldo Negativo</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filter-bar">
        <div style={{ flex: 2, minWidth: 160 }}>
          <label className="form-label">Funcionário</label>
          <select className="form-select" value={filtros.funcionario} onChange={e => setFiltros(f => ({ ...f, funcionario: e.target.value }))}>
            <option value="">Todos</option>
            {funcs.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 150 }}>
          <label className="form-label">Departamento</label>
          <select className="form-select" value={filtros.departamento} onChange={e => setFiltros(f => ({ ...f, departamento: e.target.value }))}>
            <option value="">Todos</option>
            {departamentos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 140 }}>
          <label className="form-label">De</label>
          <input type="date" className="form-control" value={filtros.data_inicio} onChange={e => setFiltros(f => ({ ...f, data_inicio: e.target.value }))} />
        </div>
        <div style={{ minWidth: 140 }}>
          <label className="form-label">Até</label>
          <input type="date" className="form-control" value={filtros.data_fim} onChange={e => setFiltros(f => ({ ...f, data_fim: e.target.value }))} />
        </div>
        <div style={{ minWidth: 150 }}>
          <label className="form-label">Classificação</label>
          <select className="form-select" value={filtros.classificado} onChange={e => setFiltros(f => ({ ...f, classificado: e.target.value }))}>
            <option value="">Todos</option>
            <option value="nao">Pendentes</option>
            <option value="sim">Classificados</option>
          </select>
        </div>
        <div style={{ alignSelf: 'flex-end', display: 'flex', gap: 6 }}>
          {[
            { key: 'data',        icon: 'bi-calendar3',         label: 'Por Data' },
            { key: 'funcionario', icon: 'bi-person-lines-fill', label: 'Por Funcionário' },
          ].map(op => (
            <button key={op.key} type="button"
              onClick={() => setAgruparPor(v => v === op.key ? '' : op.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 9px', borderRadius: 6, fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
                border: `1px solid ${agruparPor === op.key ? '#bfdbfe' : '#e2e8f0'}`,
                background: agruparPor === op.key ? '#eff6ff' : '#f8fafc',
                color: agruparPor === op.key ? '#1d4ed8' : '#64748b',
              }}>
              <i className={`bi ${op.icon}`} />
              {op.label}
            </button>
          ))}
          <button onClick={carregar} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 600 }}>
            <i className="bi bi-search" /> Buscar
          </button>
          {listaFiltrada.length > 0 && (
            <button onClick={gerarPDF} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff1f2', border: '1px solid #fecaca', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#dc2626', fontSize: '0.72rem', fontWeight: 600 }}>
              <i className="bi bi-file-earmark-pdf-fill" /> Exportar PDF
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="card-ponto">
        <div className="table-responsive">
          <table className="table-ponto" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 32 }}>Data</th>
                <th>Funcionário</th>
                <th style={{ textAlign: 'center' }}>Realizado</th>
                <th style={{ textAlign: 'center' }}>Saldo</th>
                <th>Status</th>
                <th>Classificação</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-3"><span className="spinner-border spinner-border-sm text-primary" /></td></tr>
              )}
              {!loading && listaFiltrada.map((r, idx) => {
                const prevItem = idx > 0 ? listaFiltrada[idx - 1] : null;
                const novaData = agruparPor === 'data' && r.data !== (prevItem?.data ?? null);
                const novaFunc = agruparPor === 'funcionario' && r.funcionario_nome !== (prevItem?.funcionario_nome ?? null);
                const mostrarSep = novaData || novaFunc;
                const sepLabel = agruparPor === 'funcionario' ? r.funcionario_nome : r.data;
                const qtdGrupo = agruparPor === 'funcionario'
                  ? listaFiltrada.filter(x => x.funcionario_nome === r.funcionario_nome).length
                  : listaFiltrada.filter(x => x.data === r.data).length;
                const sepSub = agruparPor === 'funcionario'
                  ? `${qtdGrupo} registro${qtdGrupo > 1 ? 's' : ''}`
                  : `${qtdGrupo} registro${qtdGrupo > 1 ? 's' : ''}`;
                return (
                <React.Fragment key={r.id}>
                  {mostrarSep && (
                    <tr>
                      <td colSpan={7} style={{ padding: '6px 10px', background: '#f1f5f9', borderTop: idx > 0 ? '2px solid #cbd5e1' : 'none', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', letterSpacing: '0.03em' }}>
                          {sepLabel}
                        </span>
                        <span style={{ marginLeft: 8, fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500 }}>
                          {sepSub}
                        </span>
                      </td>
                    </tr>
                  )}
                <tr>
                  <td style={{ whiteSpace: 'nowrap', color: '#94a3b8', fontSize: '0.78rem', paddingLeft: 32 }}>{r.data}</td>
                  <td style={{ maxWidth: 220 }}>{r.funcionario_nome}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--blue)' }}>
                    {r.horas_trabalhadas != null ? decToHHMM(r.horas_trabalhadas) : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      onClick={() => abrirSaldo(r)}
                      title={r.saldo_tipo ? `Classificado: ${TIPO_LABELS[r.saldo_tipo] || r.saldo_tipo} — clique para editar` : 'Clique para classificar'}
                      style={{
                        background: '#fee2e2', color: '#991b1b',
                        borderRadius: 3, padding: '2px 8px', fontSize: '0.75rem', fontWeight: 700,
                        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                        outline: r.saldo_tipo ? '2px solid #dc2626' : 'none', outlineOffset: 1,
                      }}
                    >
                      {decToHHMM(r.saldo_horas)}
                      {r.saldo_tipo && <i className="bi bi-check2" style={{ fontSize: '0.65rem' }} />}
                    </span>
                  </td>
                  <td><span className={`badge-status badge-status-${r.status}`}>{r.status_display}</span></td>
                  <td>
                    {r.saldo_tipo ? (
                      <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600 }}>
                        <i className="bi bi-check-circle-fill me-1" style={{ fontSize: '0.65rem' }} />
                        {TIPO_LABELS[r.saldo_tipo] || r.saldo_tipo}
                      </span>
                    ) : (
                      <span style={{ background: '#fef9c3', color: '#92400e', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600 }}>
                        <i className="bi bi-clock me-1" style={{ fontSize: '0.65rem' }} />
                        Pendente
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {(() => {
                      const proposta = propostasPorRegistro[r.id];
                      const temPropostaPendente = proposta && proposta.autorizado === false && !(proposta.observacao || '').includes('[REPROVADA]');
                      return (
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          <button
                            onClick={() => abrirProposta(r)}
                            title="Enviar proposta de compensação"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 600 }}
                          >
                            <i className="bi bi-clipboard2-plus" /> Proposta
                          </button>
                          {temPropostaPendente && (
                            <button
                              onClick={() => cancelarProposta(r)}
                              title="Cancelar proposta pendente"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff1f2', border: '1px solid #fecaca', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#dc2626', fontSize: '0.72rem', fontWeight: 600 }}
                            >
                              <i className="bi bi-x-circle" /> Cancelar
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                </React.Fragment>
                );
              })}
              {!loading && listaFiltrada.length === 0 && (
                <tr><td colSpan={7}><div className="empty-state"><i className="bi bi-check-circle" /><p>Nenhum saldo negativo encontrado</p></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal Proposta (funcionário) ── */}
      {propostaModal && propostaReg && (() => {
        const saldoMin = Math.abs(Math.round(propostaReg.saldo_horas * 60));
        const totalComp = propostaComps.reduce((s, c) => s + (c.data ? (parseInt(c.minutos) || 0) : 0), 0);
        const pct = saldoMin > 0 ? Math.min(100, Math.round(totalComp / saldoMin * 100)) : 0;
        const completo = totalComp >= saldoMin;
        const hojeISO = hoje();
        const datasUsadas = new Set();
        const errosData = propostaComps.map(c => {
          if (!c.data) return null;
          if (c.data < hojeISO) return 'Data retroativa';
          if (feriadosSet.has(c.data)) return 'Feriado';
          if (datasUsadas.has(c.data)) return 'Data duplicada';
          datasUsadas.add(c.data);
          return null;
        });
        const temErros = errosData.some(e => e !== null);
        return (
          <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setPropostaModal(false)}>
            <div className="modal-box" style={{ maxWidth: 480 }}>
              <div className="modal-header-custom" style={{ background: 'linear-gradient(135deg,#1e3a8a,#2563eb)' }}>
                <div>
                  <h5 style={{ color: '#fff', margin: 0 }}>
                    <i className="bi bi-clipboard2-plus me-2" />Proposta de Compensação
                  </h5>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', marginTop: 3 }}>
                    {propostaReg.funcionario_nome} — {propostaReg.data} &nbsp;|&nbsp;
                    <strong style={{ color: '#fff' }}>{decToHHMM(propostaReg.saldo_horas)}</strong>
                  </div>
                </div>
                <button className="modal-close" style={{ color: '#fff' }} onClick={() => setPropostaModal(false)}><i className="bi bi-x-lg" /></button>
              </div>
              <div className="modal-body-custom">
                {/* Motivo da ausência — toggle principal */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Motivo da Ausência
                  </div>
                  <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    {[
                      { v: 'JUSTIFICADO',   label: 'Justificado',     bg: '#fef9c3', color: '#854d0e', icon: 'bi-check-circle-fill' },
                      { v: 'INJUSTIFICADO', label: 'Não Justificado', bg: '#fee2e2', color: '#991b1b', icon: 'bi-x-circle-fill' },
                    ].map(op => (
                      <button key={op.v} type="button"
                        onClick={() => setPropostaMotivoAusencia(op.v)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          padding: '8px 0', border: 'none', cursor: 'pointer',
                          fontSize: '0.78rem', fontWeight: 700,
                          background: propostaMotivoAusencia === op.v ? op.bg : '#f8fafc',
                          color:      propostaMotivoAusencia === op.v ? op.color : '#94a3b8',
                          borderRight: op.v === 'JUSTIFICADO' ? '1px solid #e2e8f0' : 'none',
                          transition: 'all 0.15s',
                        }}
                      >
                        <i className={`bi ${op.icon}`} />{op.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── JUSTIFICADO: upload + período + preview ── */}
                {propostaMotivoAusencia === 'JUSTIFICADO' && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {/* Upload */}
                    <div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#166534', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="bi bi-paperclip" />Documento comprobatório
                      </div>
                      <input type="file" className="form-control form-control-sm"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={e => setPropostaDocumento(e.target.files[0] || null)}
                      />
                      {propostaDocumento && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <span style={{ fontSize: '0.68rem', color: '#15803d', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className="bi bi-file-earmark-check" />{propostaDocumento.name}
                          </span>
                          <button type="button"
                            onClick={() => window.open(URL.createObjectURL(propostaDocumento), '_blank')}
                            style={{ fontSize: '0.68rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: '#1d4ed8', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <i className="bi bi-eye" />Pré-visualizar
                          </button>
                        </div>
                      )}
                      {!propostaDocumento && propostaDocumentoExistente && (
                        <div style={{ marginTop: 6 }}>
                          <a href={`http://localhost:8000${propostaDocumentoExistente}`} target="_blank" rel="noreferrer"
                            style={{ fontSize: '0.72rem', color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                            <i className="bi bi-file-earmark-arrow-down" />Ver documento já enviado
                          </a>
                          <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: 2 }}>Selecione um novo arquivo para substituir.</div>
                        </div>
                      )}
                    </div>

                    {/* Período coberto pelo documento */}
                    <div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#166534', marginBottom: 8 }}>
                        <i className="bi bi-calendar-range me-1" />Período justificado pelo documento
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#166534', fontWeight: 600, display: 'block', marginBottom: 2 }}>Data início *</label>
                          <input type="date" className="form-control form-control-sm"
                            value={propostaDocPeriodo.data_inicio}
                            onChange={e => setPropostaDocPeriodo(p => ({ ...p, data_inicio: e.target.value }))} />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#166534', fontWeight: 600, display: 'block', marginBottom: 2 }}>Hora início</label>
                          <input type="time" className="form-control form-control-sm"
                            value={propostaDocPeriodo.hora_inicio}
                            onChange={e => setPropostaDocPeriodo(p => ({ ...p, hora_inicio: e.target.value }))} />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#166534', fontWeight: 600, display: 'block', marginBottom: 2 }}>Data fim *</label>
                          <input type="date" className="form-control form-control-sm"
                            value={propostaDocPeriodo.data_fim}
                            onChange={e => setPropostaDocPeriodo(p => ({ ...p, data_fim: e.target.value }))} />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#166534', fontWeight: 600, display: 'block', marginBottom: 2 }}>Hora fim</label>
                          <input type="time" className="form-control form-control-sm"
                            value={propostaDocPeriodo.hora_fim}
                            onChange={e => setPropostaDocPeriodo(p => ({ ...p, hora_fim: e.target.value }))} />
                        </div>
                      </div>
                    </div>

                    {/* Card de comparação horas */}
                    {(() => {
                      const devidasMin = Math.abs(Math.round((propostaReg?.saldo_horas || 0) * 60));
                      let docMin = null;
                      if (propostaDocPeriodo.hora_inicio && propostaDocPeriodo.hora_fim) {
                        const [hI, mI] = propostaDocPeriodo.hora_inicio.split(':').map(Number);
                        const [hF, mF] = propostaDocPeriodo.hora_fim.split(':').map(Number);
                        let diff = (hF * 60 + mF) - (hI * 60 + mI);
                        if (propostaDocPeriodo.data_inicio && propostaDocPeriodo.data_fim && propostaDocPeriodo.data_fim !== propostaDocPeriodo.data_inicio) {
                          const d1 = new Date(propostaDocPeriodo.data_inicio + 'T00:00:00');
                          const d2 = new Date(propostaDocPeriodo.data_fim + 'T00:00:00');
                          const extraDays = Math.max(0, (d2 - d1) / 86400000);
                          diff += extraDays * 8 * 60;
                        }
                        if (diff > 0) docMin = diff;
                      }
                      const saldoMin = docMin !== null ? docMin - devidasMin : null;
                      const pctDoc = docMin !== null && devidasMin > 0 ? Math.min(100, Math.round(docMin / devidasMin * 100)) : 0;
                      const cobre = saldoMin !== null && saldoMin >= 0;

                      return (
                        <div style={{ background: '#fff', border: '1px solid #bbf7d0', borderRadius: 10, overflow: 'hidden' }}>
                          {/* Linha de métricas */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid #f0fdf4' }}>
                            {[
                              { label: 'Horas devidas', min: devidasMin, color: '#dc2626', bg: '#fef2f2', icon: 'bi-hourglass-split' },
                              { label: 'Horas do doc.', min: docMin, color: '#2563eb', bg: '#eff6ff', icon: 'bi-file-earmark-check' },
                              { label: 'Saldo', min: saldoMin, color: saldoMin === null ? '#94a3b8' : cobre ? '#16a34a' : '#c2410c', bg: saldoMin === null ? '#f8fafc' : cobre ? '#f0fdf4' : '#fff7ed', icon: saldoMin === null ? 'bi-dash-circle' : cobre ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill' },
                            ].map((item, idx) => (
                              <div key={idx} style={{ background: item.bg, padding: '10px 12px', textAlign: 'center', borderRight: idx < 2 ? '1px solid #f0fdf4' : 'none' }}>
                                <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{item.label}</div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                                  <i className={`bi ${item.icon}`} style={{ color: item.color, fontSize: '0.9rem' }} />
                                  <span style={{ fontSize: '1.1rem', fontWeight: 800, color: item.color, lineHeight: 1 }}>
                                    {item.min === null ? '—' : (() => {
                                      const absMin = Math.abs(item.min);
                                      const h = Math.floor(absMin / 60), m = absMin % 60;
                                      return `${item.min < 0 ? '-' : ''}${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
                                    })()}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* Barra de cobertura */}
                          <div style={{ padding: '8px 12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                              <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {docMin === null ? 'Informe hora início e fim do documento' : cobre ? 'Documento cobre o período completo' : `Faltam ${decToHHMM((devidasMin - (docMin||0)) / 60)} para cobrir`}
                              </span>
                              {docMin !== null && <span style={{ fontSize: '0.65rem', fontWeight: 800, color: cobre ? '#16a34a' : '#c2410c' }}>{pctDoc}%</span>}
                            </div>
                            <div style={{ background: '#e2e8f0', borderRadius: 99, height: 5, overflow: 'hidden' }}>
                              <div style={{ width: `${pctDoc}%`, height: '100%', borderRadius: 99, background: cobre ? 'linear-gradient(90deg,#16a34a,#4ade80)' : 'linear-gradient(90deg,#f59e0b,#fb923c)', transition: 'width 0.4s ease' }} />
                            </div>
                          </div>
                          {/* Nota */}
                          <div style={{ padding: '0 12px 8px', fontSize: '0.62rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className="bi bi-info-circle" />
                            O gestor irá analisar e decidir se abona ou solicita compensação da diferença. Um afastamento será criado automaticamente.
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ── NÃO JUSTIFICADO: datas de compensação ── */}
                {propostaMotivoAusencia === 'INJUSTIFICADO' && (
                  <>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '0.78rem', color: '#1e3a8a' }}>
                      <i className="bi bi-info-circle-fill me-1" />
                      Informe a(s) data(s) em que você pretende compensar as horas. A proposta será enviada para aprovação do gestor.
                    </div>

                    {/* Barra de progresso */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Proposto</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                            <span style={{ fontSize: '1.25rem', fontWeight: 800, color: completo ? '#16a34a' : totalComp > 0 ? '#f59e0b' : '#64748b', lineHeight: 1 }}>{decToHHMM(totalComp / 60)}</span>
                            <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>de {decToHHMM(saldoMin / 60)}</span>
                          </div>
                        </div>
                        {completo
                          ? <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}><i className="bi bi-check-circle-fill" />Completo</span>
                          : <span style={{ fontSize: '1rem', fontWeight: 800, color: '#dc2626' }}>-{decToHHMM(Math.max(0, saldoMin - totalComp) / 60)}</span>
                        }
                      </div>
                      <div style={{ background: '#e2e8f0', borderRadius: 99, height: 6, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: completo ? 'linear-gradient(90deg,#16a34a,#4ade80)' : 'linear-gradient(90deg,#f59e0b,#fbbf24)', transition: 'width 0.3s ease' }} />
                      </div>
                      <div style={{ textAlign: 'right', fontSize: '0.62rem', color: '#94a3b8', marginTop: 4, fontWeight: 600 }}>{pct}%</div>
                    </div>

                    {/* Linhas de data */}
                    {propostaComps.map((c, i) => {
                      const isSex = isSexta(c.data);
                      const maxMin = isSex ? 120 : 60;
                      const outrosMin = propostaComps.reduce((s, c2, j) => j !== i ? s + (parseInt(c2.minutos) || 0) : s, 0);
                      const maxEfetivo = Math.min(maxMin, Math.max(0, saldoMin - outrosMin));
                      const minsOk = parseInt(c.minutos) <= maxEfetivo;
                      const isLast = i === propostaComps.length - 1;
                      return (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <input type="date" className="form-control date-picker-visible" value={c.data}
                              onChange={e => updatePropostaComp(i, 'data', e.target.value)}
                              style={errosData[i] ? { borderColor: '#dc2626', boxShadow: '0 0 0 2px rgba(220,38,38,0.15)' } : isLast && !c.data ? { borderColor: '#f59e0b', boxShadow: '0 0 0 2px rgba(245,158,11,0.15)' } : {}} />
                            {errosData[i] && <div style={{ fontSize: '0.65rem', color: '#dc2626', marginTop: 2 }}><i className="bi bi-exclamation-triangle-fill me-1" />{errosData[i]}</div>}
                          </div>
                          <div style={{ width: 110 }}>
                            <div style={{ position: 'relative' }}>
                              <input type="number" className="form-control" min={1} max={maxEfetivo}
                                style={{ borderColor: !minsOk ? '#dc2626' : undefined, paddingRight: 34 }}
                                value={c.minutos} onChange={e => updatePropostaComp(i, 'minutos', e.target.value)} />
                              <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#94a3b8', pointerEvents: 'none' }}>min</span>
                            </div>
                            {!minsOk && <div style={{ fontSize: '0.65rem', color: '#dc2626', marginTop: 2 }}>Máx {maxEfetivo} min</div>}
                          </div>
                          <div style={{ width: 64, display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button type="button" onClick={() => removePropostaComp(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px 6px', borderRadius: 4 }}>
                              <i className="bi bi-trash" />
                            </button>
                            {isLast && (
                              <button type="button" onClick={addPropostaComp} disabled={!c.data || totalComp >= saldoMin}
                                style={{ background: 'none', border: '1px solid', borderColor: (!c.data || totalComp >= saldoMin) ? '#e2e8f0' : '#16a34a', color: (!c.data || totalComp >= saldoMin) ? '#cbd5e1' : '#16a34a', cursor: (!c.data || totalComp >= saldoMin) ? 'not-allowed' : 'pointer', padding: '4px 6px', borderRadius: 4 }}>
                                <i className="bi bi-plus-lg" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {temErros && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', marginTop: 4, fontSize: '0.75rem', color: '#991b1b', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="bi bi-exclamation-triangle-fill" style={{ flexShrink: 0 }} />
                        Corrija as datas inválidas antes de enviar.
                      </div>
                    )}
                  </>
                )}

                {/* Sem motivo selecionado */}
                {!propostaMotivoAusencia && (
                  <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '18px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem' }}>
                    <i className="bi bi-arrow-up-circle" style={{ fontSize: '1.4rem', display: 'block', marginBottom: 6 }} />
                    Selecione o motivo da ausência acima para continuar.
                  </div>
                )}
              </div>
              <div className="modal-footer-custom" style={{ justifyContent: 'space-between' }}>
                {propostaMotivoAusencia === 'INJUSTIFICADO' ? (
                  <button type="button"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff1f2', border: '1px solid #fecaca', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#dc2626', fontSize: '0.72rem', fontWeight: 600 }}
                    onClick={() => { const sm = propostaReg ? Math.abs(Math.round(propostaReg.saldo_horas * 60)) : 0; setPropostaComps([{ data: '', minutos: Math.min(sm, 60) }]); }}
                    title="Limpar todas as datas da proposta">
                    <i className="bi bi-eraser" />Limpar
                  </button>
                ) : <span />}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#475569', fontSize: '0.72rem', fontWeight: 600 }} onClick={() => setPropostaModal(false)}>Cancelar</button>
                  <button type="button" className="btn-primary-custom" onClick={salvarProposta}
                    disabled={
                      propostaSaving ||
                      !propostaMotivoAusencia ||
                      (propostaMotivoAusencia === 'INJUSTIFICADO' && (!completo || temErros)) ||
                      (propostaMotivoAusencia === 'JUSTIFICADO' && (
                        (!propostaDocumento && !propostaDocumentoExistente) ||
                        !propostaDocPeriodo.data_inicio ||
                        !propostaDocPeriodo.data_fim
                      ))
                    }>
                    {propostaSaving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-send me-1" />Enviar</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal Classificação ── */}
      {saldoModal && saldoReg && saldoForm && (() => {
        return (
          <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setSaldoModal(false)}>
            <div className="modal-box" style={{ maxWidth: 520 }}>
              <div className="modal-header-custom" style={{ background: 'linear-gradient(135deg,#7f1d1d,#dc2626)' }}>
                <div>
                  <h5 style={{ color: '#fff', margin: 0 }}>
                    <i className="bi bi-graph-down-arrow me-2" />Saldo Negativo
                  </h5>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', marginTop: 3 }}>
                    {saldoReg.funcionario_nome} — {saldoReg.data} &nbsp;|&nbsp;
                    <strong style={{ color: '#fff' }}>{decToHHMM(saldoReg.saldo_horas)}</strong>
                  </div>
                </div>
                <button className="modal-close" style={{ color: '#fff' }} onClick={() => setSaldoModal(false)}><i className="bi bi-x-lg" /></button>
              </div>

              <div className="modal-body-custom">
                {/* Seleção de tipo */}
                <div style={{ marginBottom: 16 }}>
                  <label className="form-label" style={{ fontWeight: 600 }}>Classificação</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {TIPO_NEG.map(t => (
                      <button key={t.value} type="button" onClick={() => mudarTipo(t.value)}
                        style={{
                          flex: 1, padding: '10px 8px', borderRadius: 8, border: '2px solid',
                          borderColor: saldoForm.tipo === t.value ? '#dc2626' : '#e2e8f0',
                          background: saldoForm.tipo === t.value ? '#fef2f2' : '#f8fafc',
                          color: saldoForm.tipo === t.value ? '#7f1d1d' : '#64748b',
                          cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                        }}>
                        <i className={`bi ${t.icon}`} style={{ fontSize: '1.2rem' }} />{t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Abono */}
                {saldoForm.tipo === 'ABONO' && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 14px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label className="form-label" style={{ fontWeight: 600, color: '#7f1d1d', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <i className="bi bi-person-check-fill" />Abonado por <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input type="text" className="form-control" placeholder="Nome de quem autorizou o abono"
                        value={saldoForm.abonado_por}
                        onChange={e => setSaldoForm(f => ({ ...f, abonado_por: e.target.value }))}
                        style={{ borderColor: !saldoForm.abonado_por?.trim() ? '#fca5a5' : undefined }} />
                      {!saldoForm.abonado_por?.trim() && (
                        <div style={{ fontSize: '0.68rem', color: '#dc2626', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <i className="bi bi-exclamation-circle-fill" />Campo obrigatório
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="form-label" style={{ fontWeight: 600, color: '#7f1d1d', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <i className="bi bi-chat-left-text-fill" />Justificativa <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <textarea className="form-control" rows={2} placeholder="Motivo do abono"
                        value={saldoForm.observacao}
                        onChange={e => setSaldoForm(f => ({ ...f, observacao: e.target.value }))}
                        style={{ borderColor: !saldoForm.observacao?.trim() ? '#fca5a5' : undefined }} />
                      {!saldoForm.observacao?.trim() && (
                        <div style={{ fontSize: '0.68rem', color: '#dc2626', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <i className="bi bi-exclamation-circle-fill" />Campo obrigatório
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Ajuste de Jornada */}
                {saldoForm.tipo === 'AJUSTE_JORNADA' && (
                  <div style={{ marginBottom: 12 }}>
                    {saldoRecord && saldoRecord.tipo === 'AJUSTE_JORNADA' && (() => {
                      const rawDt = saldoRecord.atualizado_em || saldoRecord.criado_em;
                      const dt = rawDt ? new Date(rawDt) : null;
                      const dtFmt = dt && !isNaN(dt) ? dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                      return (
                        <div style={{ background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: '0.7rem', color: '#475569' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <i className="bi bi-clock-history" style={{ color: '#6366f1', fontSize: '0.9rem' }} />
                            <div>
                              <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>Ajuste em {dtFmt}</div>
                              <div>Jornada: <strong style={{ color: '#1d4ed8' }}>{ajusteHistorico?.horas || '—'}</strong></div>
                            </div>
                          </div>
                          {!(saldoRecord?.autorizado === true && (saldoRecord?.observacao || '').includes('[PROPOSTA:')) && (
                            <button type="button" onClick={async () => {
                              if (!window.confirm('Remover ajuste?')) return;
                              await deleteSaldoHora(saldoRecord.id);
                              setSaldoRecord(null); setSaldoId(null); setAjusteHistorico(null);
                              setSaldoModal(false); carregar();
                            }} style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', cursor: 'pointer', padding: '4px 8px', fontSize: '0.72rem' }}>
                              <i className="bi bi-trash me-1" />Remover
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ fontSize: '0.72rem', color: '#1d4ed8', fontWeight: 700, marginBottom: 10 }}>
                        <i className="bi bi-info-circle-fill me-1" />
                        Jornada de <strong>{jornadaAjuste?.sigla || getSigla(saldoReg.data) || '—'}</strong> — {saldoReg.data}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <label className="form-label" style={{ fontSize: '0.72rem', fontWeight: 600 }}>Horas a trabalhar neste dia</label>
                          <input type="time" className="form-control" value={ajusteHoras} onChange={e => setAjusteHoras(e.target.value)} placeholder="00:00" />
                          <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: 3 }}>Use 00:00 para dia sem jornada</div>
                        </div>
                        <button type="button" onClick={() => setAjusteHoras('00:00')}
                          style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: '0.72rem', color: '#64748b', cursor: 'pointer', marginBottom: 22 }}>
                          <i className="bi bi-x-circle me-1" />Zerar
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Compensar Depois */}
                {saldoForm.tipo === 'COMPENSAR_DEPOIS' && (
                  <div style={{ marginBottom: 12 }}>
                    <label className="form-label" style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Datas de compensação</label>
                    {(() => {
                      const pct = minSaldo > 0 ? Math.min(100, Math.round(totalComp / minSaldo * 100)) : 0;
                      const completo = totalComp >= minSaldo;
                      const restante = Math.max(0, minSaldo - totalComp);
                      return (
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
                            <div>
                              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Compensado</div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                                <span style={{ fontSize: '1.25rem', fontWeight: 800, color: completo ? '#16a34a' : totalComp > 0 ? '#f59e0b' : '#64748b', lineHeight: 1 }}>{decToHHMM(totalComp / 60)}</span>
                                <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>de {decToHHMM(minSaldo / 60)}</span>
                              </div>
                            </div>
                            {completo ? (
                              <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <i className="bi bi-check-circle-fill" />Completo
                              </span>
                            ) : (
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Restante</div>
                                <span style={{ fontSize: '1rem', fontWeight: 800, color: '#dc2626' }}>{decToHHMM(restante / 60)}</span>
                              </div>
                            )}
                          </div>
                          <div style={{ background: '#e2e8f0', borderRadius: 99, height: 6, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: completo ? 'linear-gradient(90deg,#16a34a,#4ade80)' : 'linear-gradient(90deg,#f59e0b,#fbbf24)', transition: 'width 0.3s ease' }} />
                          </div>
                          <div style={{ textAlign: 'right', fontSize: '0.62rem', color: '#94a3b8', marginTop: 4, fontWeight: 600 }}>{pct}%</div>
                        </div>
                      );
                    })()}
                    {saldoForm.compensacoes.map((c, i) => {
                      // Entrada anulada (reagendada para outra data)
                      if (c.nao_realizado) {
                        return (
                          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', opacity: 0.6 }}>
                            <div style={{ flex: 1 }}>
                              <input type="date" className="form-control date-picker-visible" value={c.data} readOnly
                                style={{ textDecoration: 'line-through', background: '#f1f5f9', color: '#94a3b8', cursor: 'default' }} />
                            </div>
                            <div style={{ width: 110 }}>
                              <div style={{ position: 'relative' }}>
                                <input type="number" className="form-control" value={c.minutos} readOnly
                                  style={{ textDecoration: 'line-through', background: '#f1f5f9', color: '#94a3b8', paddingRight: 34, cursor: 'default' }} />
                                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#94a3b8', pointerEvents: 'none' }}>min</span>
                              </div>
                            </div>
                            <div style={{ width: 64, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                              <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 4, padding: '2px 7px', fontSize: '0.62rem', fontWeight: 700, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3 }}>
                                <i className="bi bi-arrow-repeat" />
                                {c.nova_data ? (() => { const [y, m, d] = c.nova_data.split('-'); return `${d}/${m}`; })() : 'Anulado'}
                              </span>
                            </div>
                          </div>
                        );
                      }
                      // Entrada normal (editável)
                      const isSex = isSexta(c.data);
                      const maxMin = isSex ? 120 : 60;
                      const outrosMin = saldoForm.compensacoes.reduce((s, c2, j) => j !== i && !c2.nao_realizado ? s + (parseInt(c2.minutos) || 0) : s, 0);
                      const maxEfetivo = Math.min(maxMin, Math.max(0, minSaldo - outrosMin));
                      const minsOk = parseInt(c.minutos) <= maxEfetivo;
                      const isLast = i === saldoForm.compensacoes.length - 1 || !saldoForm.compensacoes.slice(i + 1).some(c2 => !c2.nao_realizado);
                      return (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <input type="date" className="form-control date-picker-visible" value={c.data}
                              onChange={e => updateComp(i, 'data', e.target.value)}
                              style={isLast && !c.data ? { borderColor: '#f59e0b', boxShadow: '0 0 0 2px rgba(245,158,11,0.15)' } : {}} />
                          </div>
                          <div style={{ width: 110 }}>
                            <div style={{ position: 'relative' }}>
                              <input type="number" className="form-control" min={1} max={maxEfetivo}
                                style={{ borderColor: !minsOk ? '#dc2626' : undefined, paddingRight: 34 }}
                                value={c.minutos} onChange={e => updateComp(i, 'minutos', e.target.value)} />
                              <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#94a3b8', pointerEvents: 'none' }}>min</span>
                            </div>
                            {!minsOk && <div style={{ fontSize: '0.65rem', color: '#dc2626', marginTop: 2 }}>Máx {maxEfetivo} min</div>}
                          </div>
                          <div style={{ width: 64, display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button type="button" onClick={() => removeComp(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px 6px', borderRadius: 4 }}>
                              <i className="bi bi-trash" />
                            </button>
                            {isLast ? (
                              <button type="button" onClick={addComp} disabled={!c.data || totalComp >= minSaldo}
                                title={!c.data ? 'Informe a data antes de adicionar' : 'Adicionar linha'}
                                style={{ background: 'none', border: '1px solid', borderColor: (!c.data || totalComp >= minSaldo) ? '#e2e8f0' : '#16a34a', color: (!c.data || totalComp >= minSaldo) ? '#cbd5e1' : '#16a34a', cursor: (!c.data || totalComp >= minSaldo) ? 'not-allowed' : 'pointer', padding: '4px 6px', borderRadius: 4 }}>
                                <i className="bi bi-plus-lg" />
                              </button>
                            ) : <span style={{ width: 30 }} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Observação */}
                {saldoForm.tipo !== 'ABONO' && (
                  <div>
                    <label className="form-label">Observação</label>
                    <textarea className="form-control" rows={2} value={saldoForm.observacao}
                      onChange={e => setSaldoForm(f => ({ ...f, observacao: e.target.value }))} placeholder="Opcional..." />
                  </div>
                )}
              </div>

              <div className="modal-footer-custom" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#475569', fontSize: '0.72rem', fontWeight: 600 }} onClick={() => setSaldoModal(false)}>Cancelar</button>
                  {saldoId && !(saldoRecord?.autorizado === true && (saldoRecord?.observacao || '').includes('[PROPOSTA:')) && (
                    <button type="button"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', color: '#dc2626', fontSize: '0.72rem', fontWeight: 600 }}
                      onClick={async () => {
                        if (!window.confirm('Remover a classificação?')) return;
                        await deleteSaldoHora(saldoId);
                        setSaldoModal(false); carregar();
                      }}>
                      <i className="bi bi-trash" />Remover classificação
                    </button>
                  )}
                </div>
                <button type="button" className="btn-primary-custom"
                  style={{ background: 'linear-gradient(135deg,#7f1d1d,#dc2626)' }}
                  disabled={saldoForm.tipo === 'ABONO' && (!saldoForm.abonado_por?.trim() || !saldoForm.observacao?.trim())}
                  onClick={salvarSaldo}>
                  <i className="bi bi-check-lg" />Salvar
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
